import Big from 'big.js';
import { Formik } from 'formik';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { Button, Buttons, CancelButton, Drawer, Field, Form } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck } from '~/hooks';
import { dateFormats, emptyStringToNull, mergeValues } from '~/utils';

export default function InvoiceMilestoneForm({ invoiceMilestoneId, projectId, onSaved, onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();

  const [project, setProject] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.www.workspaces(workspace.id).projects(projectId).get();
        setProject(data);
      } catch {
        // Do nothing
      }
    })();
  }, [api, workspace.id, projectId]);

  const [invoiceMilestone, setInvoiceMilestone] = useState(null);
  useEffect(() => {
    (async () => {
      if (!invoiceMilestoneId) {
        setInvoiceMilestone({});
        return;
      }

      try {
        const { data } = await api.www
          .workspaces(workspace.id)
          .projectSettings(projectId)
          .invoiceMilestones(invoiceMilestoneId)
          .get();
        setInvoiceMilestone(data);
      } catch {
        // Do nothing
      }
    })();
  }, [api, workspace.id, projectId, invoiceMilestoneId]);

  const [defaultInvoiceItem, setDefaultInvoiceItem] = useState();
  useEffect(() => {
    (async () => {
      if (invoiceMilestoneId || !project) return;

      try {
        // The project type can carry its own default invoice item, which wins over the workspace's.
        const { data } = await api.www
          .workspaces(workspace.id)
          .projects(projectId)
          .defaultInvoiceItem({ projectTypeId: project.projectType?.id });
        setDefaultInvoiceItem(data);
      } catch {
        // Do nothing
      }
    })();
  }, [api, workspace.id, projectId, invoiceMilestoneId, project]);

  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);

  if (!project || !invoiceMilestone || (!invoiceMilestoneId && !defaultInvoiceItem)) return null;

  const initialValues = mergeValues(
    {
      date: moment().format(dateFormats.isoDate),
      invoiceItem: null,
      details: '',
      quantity: '',
      rate: '',
      fee: '',
      type: 'custom',
      includeQuantity: invoiceMilestone?.quantity > 0,
    },
    { invoiceItem: defaultInvoiceItem, ...invoiceMilestone },
  );

  const handleSubmit = async (values, formik) => {
    formik.setSubmitting(true);
    values = emptyStringToNull({
      ..._.omit(values, ['invoiceItem', 'includeQuantity']),
      invoiceItemId: values.invoiceItem.id,
    });

    try {
      if (invoiceMilestoneId) {
        await api.www
          .workspaces(workspace.id)
          .projectSettings(projectId)
          .invoiceMilestones(invoiceMilestoneId)
          .patch(values);
      } else {
        await api.www.workspaces(workspace.id).projectSettings(projectId).invoiceMilestones().post(values);
      }

      toast.success('Invoice Milestone saved successfully.');
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(error.message);
    }

    formik.setSubmitting(false);
  };

  return (
    <Drawer
      title={invoiceMilestoneId ? 'Edit Invoice Milestone' : 'Add Invoice Milestone'}
      isOpen
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              date: Yup.date().label('Date').nullable().required(),
              invoiceItem: Yup.mixed().label('Invoice Item').required(),
              details: Yup.string().label('Notes').max(255).required(),
              quantity: Yup.number()
                .label('Quantity')
                .positive()
                .max(99999999.99)
                .nullable()
                .when('includeQuantity', (includeQuantity, schema) => (includeQuantity ? schema.required() : schema)),
              rate: Yup.number()
                .label('Rate')
                .min(-99999999999)
                .max(99999999999)
                .nullable()
                .when('includeQuantity', (includeQuantity, schema) => (includeQuantity ? schema.required() : schema)),
              fee: Yup.number().label('Amount').min(-99999999999).max(99999999999).required(),
            })}>
            {(formik) => {
              const handleIncludeQuantityChange = ({ target: { checked } }) => {
                formik.setValues({ ...formik.values, includeQuantity: checked, quantity: '', rate: '', fee: '' });
              };

              const handleCalculateFee = () => {
                const quantity = _.isNumber(formik.values.quantity)
                  ? _.round(formik.values.quantity, 2)
                  : formik.values.quantity;

                const rate = _.isNumber(formik.values.rate) ? _.round(formik.values.rate, 7) : formik.values.rate;

                const fee =
                  _.isNumber(formik.values.quantity) && _.isNumber(formik.values.rate)
                    ? Big(quantity).times(rate).round(2).toNumber()
                    : '';

                formik.setValues({ ...formik.values, fee, quantity, rate });
              };

              return (
                <>
                  <Form>
                    <Form.Section title="Details">
                      <Form.Control>
                        <Field.DayPicker name="date" placeholder="Date" clearable={false} />
                      </Form.Control>

                      <Form.Control>
                        <Field.InvoiceItemSelect
                          name="invoiceItem"
                          placeholder="Invoice Item"
                          type="income"
                          position="top"
                          clearable={false}
                        />
                      </Form.Control>

                      <Form.Control>
                        <Field.Text name="details" placeholder="Notes" maxLength={255} />
                      </Form.Control>
                    </Form.Section>

                    <Form.Section title="Billing">
                      <Form.Control>
                        <Field.Checkbox
                          name="includeQuantity"
                          label="Include a quantity and rate for this milestone"
                          onChange={handleIncludeQuantityChange}
                        />
                      </Form.Control>

                      {formik.values.includeQuantity && (
                        <Form.Control>
                          <Field.Number
                            name="quantity"
                            placeholder="Quantity"
                            precision={2}
                            onBlur={handleCalculateFee}
                          />
                          <Field.Currency
                            name="rate"
                            placeholder="Rate"
                            precision={7}
                            currency={project.currency}
                            onBlur={handleCalculateFee}
                          />
                        </Form.Control>
                      )}

                      <Form.Control>
                        <Field.Currency
                          name="fee"
                          placeholder="Amount"
                          disabled={formik.values.includeQuantity}
                          currency={project.currency}
                        />
                      </Form.Control>
                    </Form.Section>

                    <Drawer.Actions>
                      <Buttons align="right">
                        <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                        <Button isLoading={formik.isSubmitting} onClick={formik.submitForm}>
                          Save & Close
                        </Button>
                      </Buttons>
                    </Drawer.Actions>
                  </Form>
                </>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}
